import React, { createContext, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth } from './AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api/v1';
// The Socket.IO server is attached to the same HTTP server as the REST API,
// so derive its origin by stripping the `/api/v1` (or similar) path suffix.
const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || API_URL.replace(/\/api(\/v\d+)?\/?$/, '');

interface SocketContextType {
  socket: Socket | null;
  isConnected: boolean;
}

const SocketContext = createContext<SocketContextType>({ socket: null, isConnected: false });

/**
 * Owns a single Socket.IO connection for the lifetime of an authenticated
 * session. Reused by any feature that needs real-time push updates (website
 * generation progress today; enquiry notifications, publish status, etc.
 * can subscribe to their own events on the same socket later).
 */
export const SocketProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const socketRef = useRef<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [socket, setSocket] = useState<Socket | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      socketRef.current?.disconnect();
      socketRef.current = null;
      setSocket(null);
      setIsConnected(false);
      return;
    }

    const instance = io(SOCKET_URL, {
      // Re-read the token on every (re)connect attempt so a refreshed token
      // is picked up automatically without recreating the socket.
      auth: (cb) => cb({ token: localStorage.getItem('accessToken') || '' }),
      transports: ['websocket', 'polling'],
    });

    instance.on('connect', () => setIsConnected(true));
    instance.on('disconnect', () => setIsConnected(false));
    instance.on('connect_error', (err) => {
      console.warn('[socket] Connection error:', err.message);
    });

    socketRef.current = instance;
    setSocket(instance);

    return () => {
      instance.disconnect();
      socketRef.current = null;
      setSocket(null);
      setIsConnected(false);
    };
  }, [isAuthenticated]);

  return <SocketContext.Provider value={{ socket, isConnected }}>{children}</SocketContext.Provider>;
};

export const useSocket = () => useContext(SocketContext);
